// Enhanced Performance Metrics and Monitoring
// Tracks Core Web Vitals and caching efficiency

let performanceData = {
  fcp: null,
  lcp: null,
  fid: null,
  cls: null,
  ttfb: null,
  cacheHits: 0,
  cacheMisses: 0,
  totalRequests: 0,
};

// Core Web Vitals thresholds
const THRESHOLDS = {
  fcp: { good: 1800, poor: 3000 },
  lcp: { good: 2500, poor: 4000 },
  fid: { good: 100, poor: 300 },
  cls: { good: 0.1, poor: 0.25 },
  ttfb: { good: 800, poor: 1800 },
};

// Performance observer for Core Web Vitals
const observePerformance = () => {
  // First Contentful Paint
  const observeFCP = () => {
    const observer = new PerformanceObserver(list => {
      const entries = list.getEntries();
      const fcp = entries.find(entry => entry.name === 'first-contentful-paint');
      if (fcp) {
        performanceData.fcp = Math.round(fcp.startTime);
        console.log('FCP:', performanceData.fcp, 'ms');
        observer.disconnect();
      }
    });

    try {
      observer.observe({ entryTypes: ['paint'] });
    } catch (e) {
      console.warn('Paint timing not supported');
    }
  };

  // Largest Contentful Paint
  const observeLCP = () => {
    const observer = new PerformanceObserver(list => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      if (lastEntry) {
        performanceData.lcp = Math.round(lastEntry.startTime);
        console.log('LCP:', performanceData.lcp, 'ms');
      }
    });

    try {
      observer.observe({ entryTypes: ['largest-contentful-paint'] });
    } catch (e) {
      console.warn('LCP not supported');
    }
  };

  // First Input Delay
  const observeFID = () => {
    const observer = new PerformanceObserver(list => {
      const entries = list.getEntries();
      entries.forEach(entry => {
        if (entry.processingStart && entry.startTime) {
          performanceData.fid = Math.round(entry.processingStart - entry.startTime);
          console.log('FID:', performanceData.fid, 'ms');
        }
      });
    });

    try {
      observer.observe({ entryTypes: ['first-input'] });
    } catch (e) {
      console.warn('FID not supported');
    }
  };

  // Cumulative Layout Shift
  const observeCLS = () => {
    let clsValue = 0;
    let sessionValue = 0;
    let sessionEntries = [];

    const observer = new PerformanceObserver(list => {
      const entries = list.getEntries();

      for (const entry of entries) {
        if (!entry.hadRecentInput) {
          const firstSessionEntry = sessionEntries[0];
          const lastSessionEntry = sessionEntries[sessionEntries.length - 1];

          if (
            sessionValue &&
            entry.startTime - lastSessionEntry.startTime < 1000 &&
            entry.startTime - firstSessionEntry.startTime < 5000
          ) {
            sessionValue += entry.value;
            sessionEntries.push(entry);
          } else {
            sessionValue = entry.value;
            sessionEntries = [entry];
          }

          if (sessionValue > clsValue) {
            clsValue = sessionValue;
            performanceData.cls = Math.round(clsValue * 1000) / 1000;
            console.log('CLS:', performanceData.cls);
          }
        }
      }
    });

    try {
      observer.observe({ entryTypes: ['layout-shift'] });
    } catch (e) {
      console.warn('CLS not supported');
    }
  };

  // Time to First Byte
  const observeTTFB = () => {
    const observer = new PerformanceObserver(list => {
      const entries = list.getEntries();
      const navigation = entries.find(entry => entry.entryType === 'navigation');
      if (navigation) {
        performanceData.ttfb = Math.round(
          navigation.responseStart - navigation.requestStart
        );
        console.log('TTFB:', performanceData.ttfb, 'ms');
        observer.disconnect();
      }
    });

    try {
      observer.observe({ entryTypes: ['navigation'] });
    } catch (e) {
      console.warn('Navigation timing not supported');
    }
  };

  // Resource timing for cache analysis
  const observeResources = () => {
    const observer = new PerformanceObserver(list => {
      const entries = list.getEntries();

      entries.forEach(entry => {
        performanceData.totalRequests++;

        // Check if resource was served from cache
        if (entry.transferSize === 0 && entry.decodedBodySize > 0) {
          performanceData.cacheHits++;
        } else {
          performanceData.cacheMisses++;
        }

        // Log slow resources
        if (entry.duration > 1000) {
          console.warn('Slow resource:', entry.name, Math.round(entry.duration), 'ms');
        }
      });
    });

    try {
      observer.observe({ entryTypes: ['resource'] });
    } catch (e) {
      console.warn('Resource timing not supported');
    }
  };

  // Initialize all observers
  observeFCP();
  observeLCP();
  observeFID();
  observeCLS();
  observeTTFB();
  observeResources();
};

// Get performance score based on thresholds
export const getPerformanceScore = () => {
  const scores = {
    fcp: getMetricScore(performanceData.fcp, THRESHOLDS.fcp),
    lcp: getMetricScore(performanceData.lcp, THRESHOLDS.lcp),
    fid: getMetricScore(performanceData.fid, THRESHOLDS.fid),
    cls: getMetricScore(performanceData.cls, THRESHOLDS.cls),
    ttfb: getMetricScore(performanceData.ttfb, THRESHOLDS.ttfb),
  };

  const validScores = Object.values(scores).filter(score => score !== null);
  const averageScore =
    validScores.length > 0
      ? validScores.reduce((sum, score) => sum + score, 0) / validScores.length
      : 0;

  return {
    overall: Math.round(averageScore),
    individual: scores,
    cacheEfficiency: getCacheEfficiency(),
  };
};

const getMetricScore = (value, threshold) => {
  if (value === null) return null;

  if (value <= threshold.good)
    return 90 + (10 * (threshold.good - value)) / threshold.good;
  if (value <= threshold.poor)
    return 50 + (40 * (threshold.poor - value)) / (threshold.poor - threshold.good);
  return Math.max(0, 50 - ((value - threshold.poor) / threshold.poor) * 50);
};

const getCacheEfficiency = () => {
  const total = performanceData.totalRequests;
  if (total === 0) return 0;

  return Math.round((performanceData.cacheHits / total) * 100);
};

// Get current performance data
export const getPerformanceData = () => ({
  ...performanceData,
  score: getPerformanceScore(),
});

// Export performance data for debugging
export const exportPerformanceData = () => {
  const data = getPerformanceData();
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `performance-data-${new Date().toISOString().slice(0, 19)}.json`;
  link.click();

  URL.revokeObjectURL(url);
};

// Log performance summary
export const logPerformanceSummary = () => {
  const data = getPerformanceData();
  const { score } = data;

  console.group('🚀 Performance Summary');
  console.log('Overall Score:', score.overall + '/100');
  console.log('FCP:', data.fcp ? `${data.fcp}ms` : 'N/A');
  console.log('LCP:', data.lcp ? `${data.lcp}ms` : 'N/A');
  console.log('FID:', data.fid ? `${data.fid}ms` : 'N/A');
  console.log('CLS:', data.cls !== null ? data.cls : 'N/A');
  console.log('TTFB:', data.ttfb ? `${data.ttfb}ms` : 'N/A');
  console.log('Cache Efficiency:', score.cacheEfficiency + '%');
  console.log('Total Requests:', data.totalRequests);
  console.groupEnd();
};

// Initialize performance monitoring
export const initializePerformanceMonitoring = () => {
  if (typeof window === 'undefined') return;

  // Start observing immediately
  observePerformance();

  // Log summary after page load
  window.addEventListener('load', () => {
    setTimeout(() => {
      logPerformanceSummary();
    }, 3000); // Wait for all metrics to be collected
  });

  // Log summary when page becomes hidden (user navigates away)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      logPerformanceSummary();
    }
  });
};

// Manual trigger for performance logging
export const triggerPerformanceReport = () => {
  setTimeout(logPerformanceSummary, 100);
};

export default {
  getPerformanceScore,
  getPerformanceData,
  exportPerformanceData,
  logPerformanceSummary,
  initializePerformanceMonitoring,
  triggerPerformanceReport,
};
